// Core Data Models for TravelEZ Web App

// ── Subscription tier ─────────────────────────────────────────────────────────

/** Ordered tier levels — higher level = more access. */
export enum SubscriptionTier {
    Free = 'free',
    Pro = 'pro',
    Premium = 'premium',
    Family = 'family',
}

/** Numeric level for each tier, used for comparison logic. */
export const TIER_LEVELS: Record<SubscriptionTier, number> = {
    [SubscriptionTier.Free]: 0,
    [SubscriptionTier.Pro]: 1,
    [SubscriptionTier.Premium]: 2,
    [SubscriptionTier.Family]: 3,
};

/** Returns true when [userTier] grants access to [requiredTier]. */
export function tierHasAccess(userTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
    return TIER_LEVELS[userTier] >= TIER_LEVELS[requiredTier];
}

/** Safely parse a raw Firestore string into a SubscriptionTier (defaults to Free). */
export function parseTier(raw?: string | null): SubscriptionTier {
    const values = Object.values(SubscriptionTier) as string[];
    if (raw && values.includes(raw)) return raw as SubscriptionTier;
    return SubscriptionTier.Free;
}

export type FamilyRole = 'owner' | 'member';

// ── Subscription status ──────────────────────────────────────────────

/**
 * Full billing-lifecycle status of a user subscription.
 * Mirrors RevenueCat's subscriber status, including Apple/Google-specific
 * states like `grace_period` and `paused`.
 */
export type SubscriptionStatus =
    | 'active'
    | 'trialing'
    | 'grace_period'
    | 'billing_retry'
    | 'past_due'
    | 'paused'
    | 'canceled'
    | 'expired'
    | 'none';

/** Safely parse a raw Firestore string into a SubscriptionStatus. */
export function parseSubscriptionStatus(raw?: string | null): SubscriptionStatus {
    const valid: SubscriptionStatus[] = [
        'active', 'trialing', 'grace_period', 'billing_retry',
        'past_due', 'paused', 'canceled', 'expired', 'none',
    ];
    if (raw && valid.includes(raw as SubscriptionStatus)) return raw as SubscriptionStatus;
    return 'none';
}

/** Returns true when the status still grants paid-tier access. */
export function statusGrantsAccess(status: SubscriptionStatus): boolean {
    return status === 'active' || status === 'trialing' ||
        status === 'grace_period' || status === 'billing_retry' || status === 'paused';
}

/** Returns true when a payment problem exists the user should act on. */
export function statusHasPaymentIssue(status: SubscriptionStatus): boolean {
    return status === 'grace_period' || status === 'billing_retry' || status === 'past_due';
}

// ── Subscription source ──────────────────────────────────────────────

/** Which payment platform sourced the entitlement. */
export type SubscriptionSource = 'apple' | 'google' | 'paddle' | 'admin' | 'none';

/** Safely parse a raw Firestore string into a SubscriptionSource. */
export function parseSubscriptionSource(raw?: string | null): SubscriptionSource {
    const valid: SubscriptionSource[] = ['apple', 'google', 'paddle', 'admin', 'none'];
    if (raw && valid.includes(raw as SubscriptionSource)) return raw as SubscriptionSource;
    return 'none';
}

// ── User ──────────────────────────────────────────────────────────────────────

export interface User {
    id: string;
    email: string;
    /** Real email address for Apple private relay users (optional) */
    realEmail?: string;
    firstName: string;
    lastName: string;
    birthDate?: Date;
    phoneNumber?: string;
    photoUrl?: string;
    baseCurrency: string; // Default: 'USD'
    createdAt: Date;
    updatedAt: Date;

    // ── Subscription & tier ────────────────────────────────────────────────
    /** Current subscription tier (default: 'free'). */
    tier: SubscriptionTier;
    /** When the current tier was assigned. */
    tierAssignedAt?: Date;
    /** Admin userId who last assigned this tier. */
    tierAssignedBy?: string;
    /**
     * When the paid tier expires (undefined = never / manual).
     * Cloud Function writes this from RevenueCat `expiration_at_ms`.
     */
    tierExpiresAt?: Date;
    /**
     * Full billing-lifecycle status.
     * Written by `revenuecatWebhook` Cloud Function; never by the client.
     */
    subscriptionStatus: SubscriptionStatus;
    /** Which payment platform sourced the current entitlement. */
    subscriptionSource: SubscriptionSource;
    /**
     * Paddle customer ID for web Paddle transactions.
     * Set on first web checkout by `createPaddleCheckoutSession`.
     */
    paddleCustomerId?: string;
    /** Tier that will become active after pendingDowngradeAt. */
    pendingDowngradeTier?: SubscriptionTier;
    /** When the scheduled downgrade takes effect. */
    pendingDowngradeAt?: Date;

    // ── Admin / blocking ──────────────────────────────────────────────────
    /** Replaces legacy is_admin field. */
    isAdmin: boolean;
    /** Whether this account has been blocked by an admin. */
    isBlocked: boolean;
    /** Reason shown to the user when blocked. */
    blockReason?: string;

    // ── Family tier ────────────────────────────────────────────────────────
    /** ID of the family group this user belongs to (Family tier only). */
    familyGroupId?: string;
    /** Role within the family group. */
    familyRole?: FamilyRole;

    // ── Per-user feature overrides ─────────────────────────────────────────
    /** Admin-set per-feature on/off overrides, keyed by feature key. */
    featureOverrides: Record<string, boolean>;

    /** Preferred language code, e.g. 'en' or 'he'. */
    language?: string;

    // ── Installed app build (written by the mobile app on launch) ───────────
    /** Build name of the most recently seen install, e.g. '1.0.5'. */
    appVersion?: string;
    /** Build number of the most recently seen install, e.g. '42'. */
    appBuildNumber?: string;
    /** Platform of the most recently seen install: 'ios' | 'android' | 'web'. */
    appPlatform?: string;
    /** Device of the most recently seen install, e.g. 'iPhone15,2'. */
    appDeviceModel?: string;
    /** OS version of the most recently seen install, e.g. '17.2'. */
    appOsVersion?: string;
    /** When the app was last opened (any device). */
    appLastSeenAt?: Date;
    /** When the user last moved to a different app version. */
    appVersionUpdatedAt?: Date;
}

/** One device record from `users/{uid}/installations/{deviceId}`. */
export interface AppInstallation {
    id: string;
    version: string;
    buildNumber?: string;
    platform: string;
    deviceModel?: string;
    osVersion?: string;
    previousVersion?: string;
    firstSeenAt?: Date;
    lastSeenAt?: Date;
    versionUpdatedAt?: Date;
}

// ── App version helpers ───────────────────────────────────────────────────────

/**
 * Compares two dotted numeric version strings ('1.0.5' vs '1.1.0').
 * Negative if a < b, 0 if equal, positive if a > b. Mirrors the Flutter
 * VersionCheckService comparison so both sides agree on what "outdated" means.
 */
export function compareVersions(a: string, b: string): number {
    const pa = a.split('.');
    const pb = b.split('.');
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
        const na = parseInt(pa[i]?.trim() ?? '', 10) || 0;
        const nb = parseInt(pb[i]?.trim() ?? '', 10) || 0;
        if (na !== nb) return na < nb ? -1 : 1;
    }
    return 0;
}

/** Version-adoption bucket for a user, relative to the configured latest build. */
export type VersionStatus = 'latest' | 'outdated' | 'ahead' | 'unknown' | 'none';

/**
 * Buckets [version] against the latest released build for that platform.
 *
 * 'none'    — the user has never reported a build.
 * 'unknown' — they reported one, but there is no latest version configured to
 *             compare against (always the case on web, which has no store gate).
 */
export function versionStatus(version?: string, latest?: string): VersionStatus {
    if (!version) return 'none';
    if (!latest) return 'unknown';
    const cmp = compareVersions(version, latest);
    if (cmp < 0) return 'outdated';
    if (cmp > 0) return 'ahead';
    return 'latest';
}

// ── User utility functions ────────────────────────────────────────────────────

/**
 * Returns the effective email for a user - realEmail if available, otherwise email.
 * This is the email that should be used for notifications, matching, and display.
 */
export function getEffectiveEmail(user: User): string {
    return user.realEmail || user.email;
}

/**
 * Checks if a user is using Apple's private relay email forwarding.
 */
export function isUsingPrivateRelay(user: User): boolean {
    return user.email.toLowerCase().includes('@privaterelay.appleid.com');
}

// ── SubscriptionInfo ──────────────────────────────────────────────────────────

/**
 * Mirrors the `subscriptions/{userId}` Firestore document.
 *
 * **Source of truth:** RevenueCat. Firestore is a cache reconciled on every
 * login via `syncRevenueCatStatus`. Never trust `tier` without RC confirmation.
 */
export interface SubscriptionInfo {
    /** Matches the Firebase UID (also the Firestore document ID). */
    userId: string;

    // ── RevenueCat ─────────────────────────────────────────────────────────
    /** RC entitlement ID, e.g. 'pro', 'premium', 'family'. */
    revenuecatEntitlementId?: string;

    // ── Paddle (web only) ──────────────────────────────────────────────────
    paddleCustomerId?: string;
    paddleSubscriptionId?: string;
    paddlePriceId?: string;

    // ── Apple (iOS only) ───────────────────────────────────────────────────
    appleOriginalTransactionId?: string;

    // ── Google (Android only) ──────────────────────────────────────────────
    googlePurchaseToken?: string;

    // ── Status ─────────────────────────────────────────────────────────────
    /** Full lifecycle status; mirrors RC subscriber status. */
    status: SubscriptionStatus;
    /** Resolved tier (Firestore cache — RC is authoritative). */
    tier: SubscriptionTier;
    /** Which platform originated this entitlement. */
    source: SubscriptionSource;

    // ── Period ─────────────────────────────────────────────────────────────
    currentPeriodStart?: Date;
    currentPeriodEnd?: Date;
    /**
     * True when the user has cancelled; access continues until currentPeriodEnd.
     */
    cancelAtPeriodEnd: boolean;
    /** If a downgrade is scheduled, the target tier. */
    pendingDowngradeTier?: SubscriptionTier;

    // ── Trial / intro offer ────────────────────────────────────────────────
    /** Set when status === 'trialing'. */
    trialEnd?: Date;
    /**
     * True when the user qualifies for a free-trial intro offer.
     * Provided by RC; used to show "7 days free" in the UI.
     */
    introOfferEligible: boolean;

    // ── Sync ───────────────────────────────────────────────────────────────
    /**
     * Timestamp of the last RC sync.
     * Used only to rate-limit background periodic syncs;
     * login-time syncs are always unconditional.
     */
    lastSyncedAt?: Date;
}

// ── PaymentTransaction ────────────────────────────────────────────────────────

export type TransactionStatus = 'paid' | 'open' | 'void' | 'uncollectible';
export type ProductType = 'subscription' | 'consumable';

/**
 * Mirrors the `payment_transactions/{transactionId}` Firestore document.
 *
 * **Write access:** Cloud Function only. Users have read-only access.
 * All billing records originate from RevenueCat webhook events.
 */
export interface PaymentTransaction {
    id: string;
    userId: string;

    /** Which platform originated this transaction. */
    source: SubscriptionSource;

    /** Platform-specific transaction identifiers. */
    paddleTransactionId?: string;
    appleTransactionId?: string;
    googleOrderId?: string;

    /** Amount in smallest currency unit (e.g. agorot for ILS, cents for USD). */
    amount: number;
    currency: string;

    status: TransactionStatus;

    /** The subscription tier or consumable feature key this transaction covers. */
    tier?: SubscriptionTier;
    productType: ProductType;

    description?: string;

    /** Paddle-hosted invoice PDF URL (Paddle/web only; null for Apple/Google). */
    invoiceUrl?: string;

    /**
     * URL of the official Israeli tax invoice generated via Morning API.
     * Paddle (as Merchant of Record) handles VAT — reserved for additional
     * local compliance records if needed.
     * Null for Apple/Google transactions (stores handle VAT themselves).
     */
    greenInvoiceUrl?: string;

    createdAt: Date;
    paidAt?: Date;
}

// ── FeatureCredits ────────────────────────────────────────────────────────────

/**
 * Mirrors the `feature_credits/{userId}` Firestore document.
 *
 * Tracks purchased quota credits from consumable IAP purchases.
 * **Write access:** Cloud Function only (after RC NON_SUBSCRIPTION_PURCHASE events).
 */
export interface FeatureCredits {
    /** Matches the Firebase UID (also the Firestore document ID). */
    userId: string;

    /** Cumulative number of additional trips purchased. */
    extraTrips: number;

    /** Cumulative number of additional AI query tokens purchased. */
    extraAiTokens: number;

    /** Cumulative additional storage in MB purchased. */
    extraStorageMb: number;

    lastUpdatedAt?: Date;
}

export interface Country {
    name: string;
    code: string; // ISO 3166-1 alpha-2
}

export interface Region {
    id: string;
    name: string;
    country: Country;
    description?: string;
}

export interface MapLocation {
    // Support both lat/lng (mobile) and latitude/longitude (web) formats
    lat?: number;
    lng?: number;
    latitude: number;
    longitude: number;
    address?: string;
    placeId?: string; // Google Places ID
}

export interface MapData {
    center: MapLocation;
    zoom: number;
}

export interface Trip {
    id: string;
    name: string;
    ownerId: string;
    startDate: Date;
    endDate: Date;
    participantIds: string[];
    sharedToUserIds: string[];
    countries: Country[];
    mainCountry?: Country;
    regions: Region[];
    highlightImages: string[];
    mainImageIndex: number;
    map: MapData;
    defaultCurrency: string;
    currencyRates: Record<string, number>;
    mapNeedsUpdate: boolean;
    aiPlanningSessionId?: string;
    routeSummary?: string;
    totalRouteDistance?: number;
    numberOfMoves?: number;
    isArchived?: boolean; // @deprecated — kept for backwards compat during migration
    archivedByUsers?: string[]; // per-user archive — replaces isArchived
    isSharedViaLink?: boolean;
    /**
     * Set by Cloud Function when user downgrades to Free and this trip
     * exceeds the free tier limit (trips #4+, sorted by createdAt ASC).
     * Cleared on re-upgrade. Data is never deleted — trip is read-only.
     */
    isLockedByDowngrade?: boolean;
    createdAt: Date;
    updatedAt: Date;
    /**
     * "Last changed" sort key for the admin dashboard. Bumped on trip-doc edits
     * (trip.service) AND on any content subcollection write via the
     * onTripContentWrite Cloud Function — so it reflects activity/accommodation
     * edits, unlike updatedAt. May be absent on legacy trips until backfilled.
     */
    lastModifiedAt?: Date;
    /** Denormalized per-subcollection counts maintained by onTripContentWrite. */
    counts?: {
        activities?: number;
        accommodations?: number;
        flightTickets?: number;
        carRentals?: number;
        expenses?: number;
        documents?: number;
    };
    /**
     * Trip Market publication state. Written **only** by the market Cloud
     * Functions — Firestore rules reject client writes to `market.status`, so
     * never include this in a client `updateTrip` payload.
     */
    market?: TripMarket;
}

// ── Trip Market ───────────────────────────────────────────────────────────────

/**
 * Publication state of a trip in the public Trip Market.
 * Only `published` trips are readable by anonymous visitors.
 */
export type MarketStatus =
    | 'draft'
    | 'pending'
    | 'published'
    | 'rejected'
    | 'unpublished';

/**
 * Category keys the owner toggles when publishing.
 *
 * `activities` / `accommodations` / `flights` / `carRentals` / `expenses` /
 * `documents` map onto trip subcollections and are enforced by Firestore rules.
 * `regions` and `images` live on the trip document itself, so they are enforced
 * by the renderer only — the publish dialog says so explicitly.
 */
export interface TripMarketShare {
    activities: boolean;
    accommodations: boolean;
    regions: boolean;
    images: boolean;
    flights: boolean;
    carRentals: boolean;
    expenses: boolean;
    documents: boolean;
}

export type MarketShareKey = keyof TripMarketShare;

/** Toggle order used by the publish dialog. */
export const MARKET_SHARE_KEYS: MarketShareKey[] = [
    'activities', 'accommodations', 'regions', 'images',
    'flights', 'carRentals', 'expenses', 'documents',
];

/**
 * Keys carrying booking PII. Within a shared category EVERY field is readable —
 * confirmation numbers, passenger names, amounts — so the publish dialog warns
 * before these are enabled.
 */
export const MARKET_SENSITIVE_KEYS: MarketShareKey[] = [
    'flights', 'carRentals', 'expenses', 'documents',
];

/** Itinerary-only defaults a new listing starts from. */
export const DEFAULT_MARKET_SHARE: TripMarketShare = {
    activities: true,
    accommodations: true,
    regions: true,
    images: true,
    flights: false,
    carRentals: false,
    expenses: false,
    documents: false,
};

export interface TripMarket {
    status: MarketStatus;
    share: TripMarketShare;
    title: string;
    summary: string;
    tags: string[];
    language?: string;
    coverImageUrl?: string;
    submittedAt?: Date;
    submittedBy?: string;
    reviewedAt?: Date;
    reviewedBy?: string;
    rejectionReason?: string;
    publishedAt?: Date;
    viewCount?: number;
    cloneCount?: number;
}

/**
 * A row in the public `market_listings` catalog — denormalized from the trip by
 * the market Cloud Functions so the browse page never reads `trips/*`.
 *
 * Sort/filter only on fields this catalog owns (`publishedAt`, `cloneCount`):
 * dates copied from a trip have mixed Timestamp/ISO-string types across the
 * Flutter and web clients and break range filters silently.
 */
export interface MarketListing {
    tripId: string;
    status: MarketStatus;
    title: string;
    summary: string;
    coverImageUrl?: string;
    tags: string[];
    language?: string;
    countries: Country[];
    mainCountry?: Country;
    durationDays: number;
    counts: { activities?: number; accommodations?: number };
    ownerDisplayName: string;
    ownerPhotoUrl?: string;
    share: TripMarketShare;
    publishedAt?: Date;
    updatedAt?: Date;
    viewCount: number;
    cloneCount: number;
}

export enum ActivityType {
    HIKING = 'hiking',
    VIEWPOINT = 'viewpoint',
    ATTRACTION = 'attraction',
    SHOPPING = 'shopping',
    DINING = 'dining',
    MUSEUM = 'museum',
    TRANSPORT = 'transport',
    OTHER = 'other',
}

export interface Activity {
    id: string;
    tripId: string;
    title: string;
    description?: string;
    date?: Date;
    /** End date for multi-day activities (e.g. a multi-day trek). When set, `date` is the start day. */
    endDate?: Date;
    type: ActivityType;
    highlightImages: string[];
    regions: Region[];
    location?: MapLocation;
    durationHours?: number;
    website?: string;
    openingTime?: string;
    notes?: string;
    price?: number;
    currency?: string;
    isBooked: boolean;
    isSelected: boolean;
    isPaid: boolean;
    tags: string[];
    order?: number;
    // Set when created from a forwarded booking email — links back to the
    // original mail (raw_emails/{sourceEmailId}) and its stored attachments
    // (e.g. the event ticket PDF/image), mirroring flights/accommodations.
    sourceEmailId?: string;
    attachmentInfos?: EmailAttachmentInfo[];
    createdAt: Date;
    updatedAt: Date;
}

export enum AccommodationType {
    HOTEL = 'hotel',
    CAMPSITE = 'campsite',
    APARTMENT = 'apartment',
    HOSTEL = 'hostel',
    VILLA = 'villa',
    GUESTHOUSE = 'guesthouse',
    RESORT = 'resort',
    AIRBNB = 'airbnb',
    OTHER = 'other',
}

export interface Accommodation {
    id: string;
    tripId: string;
    name: string;
    website?: string;
    type: AccommodationType;
    price: number;
    currency: string;
    checkInDate?: Date;
    checkOutDate?: Date;
    cancelUntil?: Date;
    location?: MapLocation;
    region?: Region;
    isIncludeBreakfast: boolean;
    isBooked: boolean;
    isPaid: boolean;
    images?: string[];
    notes?: string;
    address?: string;
    bookingReference?: string;
    breakfastIncluded?: boolean;
    freeCancellation?: boolean;
    cancellationDeadline?: Date;
    latitude?: number;
    longitude?: number;
    // Booking confirmation number (mirrors mobile Accommodation.originalId).
    // Used to dedup accommodations created from email bookings.
    originalId?: string;
    // Set when created from a forwarded booking email — links back to the
    // original mail (raw_emails/{sourceEmailId}) and its stored attachments.
    sourceEmailId?: string;
    attachmentInfos?: EmailAttachmentInfo[];
    createdAt: Date;
    updatedAt: Date;
}

export enum FlightStatus {
    SCHEDULED = 'scheduled',
    ON_TIME = 'onTime',
    DELAYED = 'delayed',
    CANCELLED = 'cancelled',
    DEPARTED = 'departed',
    ARRIVED = 'arrived',
    UNKNOWN = 'unknown',
}

export interface FlightTicket {
    id: string;
    tripId: string;
    title: string;
    fromLocation: string;
    toLocation: string;
    firstName: string;
    lastName: string;
    flightNumber?: string;
    airline?: string;
    reservationNumber?: string; // Reservation number / PNR
    departureTime?: Date;
    arrivalTime?: Date;
    gate?: string;
    terminal?: string;
    seat?: string;
    status: FlightStatus;
    price?: number;
    currency?: string;
    isPaid?: boolean;
    pkpassData?: string; // Base64 encoded .pkpass file
    connectedUserId?: string;
    // Set when the flight was created from a forwarded booking email — lets the
    // UI link back to the original mail (raw_emails/{sourceEmailId}) and open the
    // stored ticket attachments.
    sourceEmailId?: string;
    attachmentInfos?: EmailAttachmentInfo[];
    uploadedBy: string;
    createdAt: Date;
    updatedAt: Date;
}

export interface CarRental {
    id: string;
    tripId: string;
    userId: string;
    company: string; // Required
    vehicleType: string;
    pickupLocation: string;
    returnLocation: string;
    pickupDate: Date; // Required
    returnDate: Date; // Required
    confirmationNumber: string;
    driverName: string;
    price: number;
    currency: string;
    notes?: string;
    isPaid: boolean;
    pickupMapLocation?: MapLocation;
    dropOffMapLocation?: MapLocation;
    // Set when created from a forwarded booking email — links back to the
    // original mail (raw_emails/{sourceEmailId}) and its stored attachments.
    sourceEmailId?: string;
    attachmentInfos?: EmailAttachmentInfo[];
    createdAt: Date;
    updatedAt: Date;
}

export enum ExpenseCategory {
    ACCOMMODATION = 'accommodation',
    CAR_RENTAL = 'carRental',
    FLIGHT = 'flight',
    GROCERIES = 'groceries',
    MEALS = 'restaurantBar',
    SHOPPING = 'shopping',
    TRANSPORTATION = 'transport',
    INSURANCE = 'insurance',
    FUEL = 'gas',
    PARKING = 'parking',
    COMMUNICATION = 'communication',
    ACTIVITIES = 'attraction',
    MEDICAL = 'health',
    VISAS = 'visa',
    OTHER = 'other',
    CUSTOM = 'custom',
    // Legacy aliases for backward compatibility
    FOOD = 'restaurantBar',
    TOLLS = 'parking',
    ENTERTAINMENT = 'attraction',
    HEALTH = 'health',
    VISA = 'visa',
}

export enum PaymentMethod {
    CARD = 'card',
    CASH = 'cash',
    // Legacy aliases for backward compatibility
    CREDIT_CARD = 'card',
    DEBIT_CARD = 'card',
    ONLINE_TRANSFER = 'card',
    OTHER = 'cash',
}

export interface Expense {
    id: string;
    tripId: string;
    amount: number;
    currency: string;
    category: ExpenseCategory;
    customCategory?: string;
    paymentMethod: PaymentMethod;
    paidBy: string; // User ID
    splitWith: string[]; // Array of participant IDs
    date: Date;
    description?: string;
    notes?: string;
    relatedActivityId?: string;
    relatedAccommodationId?: string;
    relatedFlightId?: string;
    relatedCarRentalId?: string;
    relatedFlightTicketId?: string;
    createdAt: Date;
    updatedAt: Date;
}

export enum BookingType {
    ACCOMMODATION = 'accommodation',
    FLIGHT = 'flight',
    CAR_RENTAL = 'carRental',
    ACTIVITY = 'activity',
    UNRECOGNIZED = 'unrecognized',
    UNKNOWN = 'unknown',
}

export enum ApprovalStatus {
    PENDING = 'pending',
    APPROVED = 'approved',
    REJECTED = 'rejected',
    IGNORED = 'ignored',
}

export interface FlightSegment {
    airline?: string;
    flightNumber?: string;
    fromLocation?: string;
    toLocation?: string;
    departureTime?: Date;
    arrivalTime?: Date;
    gate?: string;
    terminal?: string;
    seat?: string;
}

export interface FlightDetails {
    segments: FlightSegment[];
    airline?: string;
    flightNumber?: string;
    fromLocation?: string;
    toLocation?: string;
    departureTime?: Date;
    arrivalTime?: Date;
}

export interface CarRentalDetails {
    company?: string;
    vehicleType?: string;
    pickupLocation?: string;
    returnLocation?: string;
    pickupDate?: Date;
    returnDate?: Date;
    confirmationNumber?: string;
    driverName?: string;
}

export interface EmailBooking {
    id: string;
    userId: string;
    emailId: string;
    bookingType: BookingType;
    propertyName?: string;
    city?: string;
    country?: string;
    airline?: string;
    checkInDate?: Date;
    checkOutDate?: Date;
    totalPrice?: number;
    currency?: string;
    confirmationNumber?: string;
    vendor?: string;
    propertyAddress?: string;
    guestName?: string;
    guestEmail?: string;
    flightDetails?: FlightDetails;
    carRentalDetails?: CarRentalDetails;
    parsingConfidence?: number;
    approvalStatus: ApprovalStatus;
    tripId?: string; // Assigned trip
    location?: MapLocation;
    rawEmailSubject?: string;
    rawEmailSender?: string;
    notes?: string;
    attachmentInfos?: EmailAttachmentInfo[];
    createdAt: Date;
    updatedAt: Date;
}

export interface EmailAttachmentInfo {
    filename: string;
    url: string;
    mimeType: string;
    size: number;
}

export interface TripDocument {
    id: string;
    tripId: string;
    title: string;
    documentType: 'ticket' | 'booking' | 'insurance' | 'misc';
    fileUrl?: string;
    linkUrl?: string;
    expiryDate?: Date;
    notes?: string;
    createdAt: Date;
    updatedAt: Date;
}

export interface EquipmentList {
    id: string;
    tripId: string;
    name: string;
    emoji?: string;
    items: EquipmentItem[];
    createdAt: Date;
    updatedAt: Date;
}

/** Flat item document — mirrors mobile: trips/{tripId}/equipment/{itemId} */
export interface EquipmentItemDoc {
    id: string;
    group: string;
    name: string;
    checked: boolean;
    order: number;
}

export interface EquipmentUserTemplate {
    id: string;
    name: string;
    groups: { name: string; emoji: string; items: string[] }[];
    createdAt: Date;
}

export interface EquipmentItem {
    id: string;
    name: string;
    checked: boolean;
    category?: string;
}

export interface TripShareToken {
    id: string;
    tripId: string;
    code: string; // 6-character code
    expiryDate?: Date;
    maxUses?: number;
    currentUses: number;
    createdAt: Date;
}

export interface Notification {
    id: string;
    userId: string;
    type: 'booking' | 'cancellation' | 'document_expiry' | 'trip_assignment' | 'general';
    title: string;
    body: string;
    isRead: boolean;
    relatedTripId?: string;
    relatedBookingId?: string;
    actionUrl?: string;
    createdAt: Date;
}

export enum UserRole {
    OWNER = 'owner',
    PARTICIPANT = 'participant',
    VIEWER = 'viewer',
    NONE = 'none',
}

export interface UserRoleInfo {
    role: UserRole;
    canEdit: boolean;
    canAdd: boolean;
    canDelete: boolean;
    canShare: boolean;
}

// Metrics and Analytics
export interface UserMetrics {
    userId: string;
    apiCalls: Record<string, number>; // e.g., { gemini: 10, places: 5 }
    storageUsage: number; // in bytes
    featureUsage: Record<string, number>;
    lastUpdated: Date;
}

export interface TripMetrics {
    tripId: string;
    apiCalls: Record<string, number>;
    costs: Record<string, number>;
    lastUpdated: Date;
}

export interface AppMetrics {
    totalApiCalls: Record<string, number>;
    totalCosts: Record<string, number>;
    activeUsers: number;
    lastUpdated: Date;
}

// AI Trip Planning
export interface TripPlannerSession {
    id: string;
    userId: string;
    currentStep: number;
    destination?: Country[];
    regions?: Region[];
    startDate?: Date;
    endDate?: Date;
    travelers?: number;
    transportMode?: string;
    travelStyle?: string;
    interests?: string[];
    preferences?: Record<string, any>;
    createdAt: Date;
    updatedAt: Date;
}

// Export Flight as alias for FlightTicket for backward compatibility
export type Flight = FlightTicket;

// ── Family group ──────────────────────────────────────────────────────────────

/** A family subscription group — up to 5 members sharing one Family-tier subscription. */
export interface FamilyGroup {
    id: string;
    /** userId of the family owner (the subscription holder). */
    ownerUserId: string;
    /** All member userIds including the owner. Max 5. */
    memberUserIds: string[];
    tier: SubscriptionTier;
    createdAt: Date;
    updatedAt: Date;
}

/** Invitation sent to join a family group. */
export interface FamilyInvitation {
    id: string;
    inviterUserId: string;
    familyGroupId: string;
    /** Email address of the invited person. */
    inviteeEmail: string;
    status: 'pending' | 'accepted' | 'declined';
    createdAt: Date;
    expiresAt?: Date;
}

// ── Usage counters ────────────────────────────────────────────────────────────

/**
 * Per-period usage snapshot stored at `users/{uid}/usage_counters/{period}`.
 * Period examples: 'daily_2026-03-08', 'monthly_2026-03', 'total'
 */
export interface UsageCounters {
    period: string;
    aiQueries: number;
    tripsCreated: number;
    placesSearches: number;
    aviationLookups: number;
    blogPostsGenerated: number;
    ocrScans: number;
    activitiesAdded: number;
    accommodationsAdded: number;
    flightsAdded: number;
    carRentalsAdded: number;
    regionsAdded: number;
    expensesAdded: number;
    participantsAdded: number;
    tripDocumentsAdded: number;
    equipmentListsCreated: number;
    jsonImports: number;
    updatedAt?: Date;
}

/** Tier-definitions feature entry as stored in `app_config/tier_definitions`. */
export interface FeatureDefinition {
    /** Minimum tier level required (0=free, 1=pro, 2=premium, 3=family, -1=admin). */
    min_tier: number;
    /** Per-tier usage limits (-1 = unlimited). Not present for boolean features. */
    limits?: { free: number; pro: number; premium: number; family: number };
    /** Reset period for counter-based features. */
    limit_period?: 'daily' | 'monthly' | 'total' | 'per_trip';
    /** When true, Family tier is denied access even though level 3 ≥ minTier. */
    family_excluded?: boolean;
}

/** Shape of the `app_config/tier_definitions` Firestore document. */
export interface TierDefinitionsDoc {
    version: number;
    updatedAt: Date;
    features: Record<string, FeatureDefinition>;
}

/** Upgrade request created by a user when they hit a feature limit. */
export interface UpgradeRequest {
    id: string;
    userId: string;
    currentTier: SubscriptionTier;
    requestedTier: SubscriptionTier;
    status: 'pending' | 'approved' | 'denied';
    /** Optional note from the user. */
    note?: string;
    /** Reason provided by admin on denial. */
    denialReason?: string;
    createdAt: Date;
    resolvedAt?: Date;
}